import { Box, Paper } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@/styles/use-finance-theme';

export interface AuthSurfaceProps {
  children: React.ReactNode;
  brand?: React.ReactNode;
  footer?: React.ReactNode;
}

/**
 * AuthSurface — canonical frame for authentication flows.
 *
 * Centers the card, respects mobile dynamic viewports and safe areas, and
 * applies the canonical auth width, padding, and elevation. All auth pages
 * should use this primitive.
 *
 * Visual treatment: subtle gradient background, bordered card with no heavy
 * elevation for a modern, clean appearance.
 */
export function AuthSurface({ children, brand, footer }: AuthSurfaceProps) {
  const theme = useTheme();
  const auth = theme.financeLayout.auth;
  const surfaceGap = theme.spacing(auth.surfaceGap);

  return (
    <Box
      component="main"
      id="main-content"
      data-testid="main-content"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        '@supports (height: 100dvh)': {
          minHeight: '100dvh',
        },
        background: (t) =>
          t.palette.mode === 'dark'
            ? `radial-gradient(ellipse at 50% 0%, ${alpha(t.palette.primary.dark, 0.08)} 0%, transparent 60%), ${t.palette.background.default}`
            : `radial-gradient(ellipse at 50% 0%, ${alpha(t.palette.primary.light, 0.09)} 0%, transparent 60%), ${t.palette.background.default}`,
        overflowY: 'auto',
        px: `max(${surfaceGap}, env(safe-area-inset-left), env(safe-area-inset-right))`,
        pt: `max(${surfaceGap}, env(safe-area-inset-top))`,
        pb: `max(${surfaceGap}, env(safe-area-inset-bottom))`,
      }}
    >
      <Paper
        sx={{
          width: '100%',
          maxWidth: auth.cardMaxWidth,
          p: { xs: auth.cardPadding.xs, sm: auth.cardPadding.sm },
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: (t) => t.shadows[2],
        }}
        elevation={0}
      >
        {brand && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {brand}
          </Box>
        )}
        {children}
        {footer && <Box sx={{ mt: 3 }}>{footer}</Box>}
      </Paper>
    </Box>
  );
}
